import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { movies } from '@/mocks/data';
import SEOHead, {
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
} from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const movie = useMemo(() => movies.find((m) => m.slug === slug), [slug]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && (m.category === movie.category || m.region === movie.region))
      .slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-800 text-foreground-500 mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-200 mb-2">影片未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该影片可能已下架或链接不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-950 rounded-lg text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: movie.region === 'japanese-movie' ? '日本电影' : '韩国电影', path: `/category/${movie.region}` },
    { name: movie.title },
  ];

  const articleSchema = generateArticleSchema({
    title: `${movie.title} - 日韩影视网 | ${movie.titleEn} 在线观看`,
    description: movie.description,
    image: movie.coverImage,
    datePublished: `${movie.year}-01-01`,
    dateModified: '2026-08-03',
    url: `https://www.rihanyingshi.com/article/${movie.slug}`,
  });

  const breadcrumbSchema = generateBreadcrumbSchema(
    breadcrumbItems.map((item) => ({
      name: item.name,
      url: item.path ? `https://www.rihanyingshi.com${item.path}` : `https://www.rihanyingshi.com/article/${movie.slug}`,
    })),
  );

  const pageSchema = generateWebPageSchema(
    `${movie.title} - 日韩影视网 | ${movie.titleEn} 在线观看`,
    movie.description.slice(0, 160),
    `https://www.rihanyingshi.com/article/${movie.slug}`,
  );

  const combinedSchema = { ...pageSchema, ...articleSchema, ...breadcrumbSchema };

  return (
    <>
      <SEOHead
        title={`${movie.title} - 日韩影视网 | ${movie.titleEn} 免费在线观看`}
        description={movie.description.slice(0, 160)}
        keywords={`日韩影视网,${movie.title},${movie.titleEn},${movie.tags.join(',')},${movie.region === 'japanese-movie' ? '日本电影' : '韩国电影'}`}
        canonicalUrl={`https://www.rihanyingshi.com/article/${movie.slug}`}
        ogImage={movie.coverImage}
        ogType="article"
        schema={combinedSchema}
        lastModified="2026-08-03"
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="py-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="relative overflow-hidden rounded-xl aspect-[2/3] max-w-[320px] mx-auto lg:max-w-none">
                    <img
                      src={movie.coverImage}
                      alt={`${movie.title} ${movie.titleEn} 电影海报`}
                      title={`${movie.title} ${movie.titleEn} - 日韩影视网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-2">
                  {movie.title}
                </h1>
                <p className="text-base md:text-lg text-foreground-400 mb-6">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="flex items-center gap-1.5 bg-accent-500/10 text-accent-400 px-3 py-1.5 rounded-full">
                    <i className="ri-star-fill text-sm"></i>
                    <span className="font-bold text-sm">{movie.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-500">{movie.year}年</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="px-2 py-1 text-xs bg-background-800 text-foreground-400 rounded-full">
                    {movie.region === 'japanese-movie' ? '日本' : '韩国'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6 p-4 rounded-lg bg-background-800/50 border border-background-700/50">
                  <div>
                    <span className="text-xs text-foreground-500 block mb-0.5">导演</span>
                    <span className="text-sm text-foreground-200 font-medium">{movie.director}</span>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 block mb-0.5">主演</span>
                    <span className="text-sm text-foreground-200 font-medium">{movie.actors.join('、')}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${movie.category}`}
                      className="px-3 py-1 text-xs bg-secondary-500/10 text-secondary-400 rounded-full hover:bg-secondary-500/20 transition-colors whitespace-nowrap"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 font-semibold rounded-lg hover:bg-primary-400 transition-all duration-200 text-sm whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill text-lg"></i> 立即播放
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 text-foreground-400 hover:text-foreground-200 hover:bg-background-700 transition-all duration-200 cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 text-foreground-400 hover:text-foreground-200 hover:bg-background-700 transition-all duration-200 cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>

                <div>
                  <h2 className="text-lg font-bold text-foreground-100 font-heading mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="pb-16 border-t border-background-700 pt-10">
                <h2 className="text-xl font-bold text-foreground-50 font-heading mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard
                      key={m.id}
                      slug={m.slug}
                      title={m.title}
                      titleEn={m.titleEn}
                      coverImage={m.coverImage}
                      rating={m.rating}
                      year={m.year}
                      category={m.category}
                      region={m.region}
                      tags={m.tags}
                    />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}